import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "./Reveal";

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/portfolio").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  if (!items.length) return null;
  const cats = ["All", ...Array.from(new Set(items.map((i) => i.category)))];
  const shown = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="bg-heirloom-bone py-24 md:py-28">
      <div className="max-w-[1300px] mx-auto px-6">
        <Reveal className="text-center mb-10">
          <span className="gold-rule overline text-heirloom-maroon">Our Work</span>
          <h2 className="font-display text-4xl md:text-5xl text-heirloom-obsidian mt-4">
            A Glimpse of Our <span className="text-heirloom-maroon italic">Celebrations</span>
          </h2>
        </Reveal>

        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {cats.map((c) => (
            <button
              key={c}
              data-testid={`portfolio-filter-${c.toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={`text-xs font-semibold tracking-[0.14em] uppercase px-5 py-2.5 rounded-full border transition-all duration-300 ${
                filter === c ? "bg-heirloom-maroon text-heirloom-bone border-heirloom-maroon" : "border-heirloom-line text-heirloom-obsidian/70 hover:border-heirloom-maroon hover:text-heirloom-maroon"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          <AnimatePresence>
            {shown.map((it, i) => (
              <motion.button
                layout
                key={it.id}
                data-testid={`portfolio-item-${i}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
                onClick={() => setLightbox(it)}
                className={`group relative overflow-hidden rounded-xl ${i % 5 === 0 ? "row-span-2 aspect-[3/4] md:aspect-auto" : "aspect-square"}`}
              >
                <img src={it.image} alt={it.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[900ms]" />
                <div className="absolute inset-0 bg-gradient-to-t from-heirloom-obsidian/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex flex-col justify-end p-4 text-left">
                  <span className="overline text-[0.55rem] text-heirloom-gold-light">{it.category}</span>
                  <span className="font-display text-lg text-heirloom-bone leading-tight mt-0.5">{it.title}</span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[80] bg-heirloom-obsidian/90 backdrop-blur-sm flex items-center justify-center p-6"
            data-testid="portfolio-lightbox"
          >
            <button className="absolute top-6 right-6 text-heirloom-bone" onClick={() => setLightbox(null)}><X size={30} /></button>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="max-w-4xl w-full">
              <img src={lightbox.image} alt={lightbox.title} className="w-full max-h-[80vh] object-contain rounded-xl" />
              <div className="text-center mt-4">
                <span className="overline text-heirloom-gold-light">{lightbox.category}</span>
                <div className="font-display text-2xl text-heirloom-bone mt-1">{lightbox.title}</div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
